// src/utils/translationService.js
import axios from 'axios';

const TRANSLATOR_ENDPOINT = process.env.REACT_APP_TRANSLATOR_ENDPOINT;
const TRANSLATOR_API_KEY = process.env.REACT_APP_TRANSLATOR_API_KEY;
const TRANSLATOR_REGION = process.env.REACT_APP_TRANSLATOR_REGION;


export const SUPPORTED_LANGUAGES = {
  ENGLISH: { code: 'en', name: 'English' },
  HINDI: { code: 'hi', name: 'हिंदी' },
  GUJARATI: { code: 'gu', name: 'ગુજરાતી' },
  KANNADA: { code: 'kn', name: 'ಕನ್ನಡ' }
};

/**
 * Translates a given text to the target language
 * @param {string} text - The text to translate
 * @param {string} targetLanguage - The language code to translate to
 * @param {string} sourceLanguage - Optional source language code (auto-detect if not provided)
 * @returns {Promise<string>} - The translated text
 */
export const translateText = async (text, targetLanguage, sourceLanguage = '') => {
  if (!text || text.trim() === '') {
    return '';
  }

  // Skip translation if target is English, which we assume is the default language
  if (targetLanguage === SUPPORTED_LANGUAGES.ENGLISH.code) {
    return text;
  }

  try {
    const response = await axios({
      baseURL: TRANSLATOR_ENDPOINT,
      url: '/translate',
      method: 'post',
      headers: {
        'Ocp-Apim-Subscription-Key': TRANSLATOR_API_KEY,
        'Ocp-Apim-Subscription-Region': TRANSLATOR_REGION,
        'Content-type': 'application/json',
      },
      params: {
        'api-version': '3.0',
        'from': sourceLanguage || undefined, // Will auto-detect if not provided
        'to': targetLanguage
      },
      data: [{
        'text': text
      }],
      responseType: 'json'
    });

    const result = response.data;
    if (result && result[0] && result[0].translations && result[0].translations.length > 0) {
      return result[0].translations[0].text;
    }
    return text; // Return original if translation failed
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original on error
  }
};

/**
 * Batch translates an array of texts to the target language
 * @param {string[]} texts - Array of texts to translate
 * @param {string} targetLanguage - The language code to translate to
 * @returns {Promise<string[]>} - Array of translated texts
 */
export const batchTranslate = async (texts, targetLanguage) => {
  if (!texts || texts.length === 0 || targetLanguage === SUPPORTED_LANGUAGES.ENGLISH.code) {
    return texts;
  }

  try {
    const response = await axios({
      baseURL: TRANSLATOR_ENDPOINT,
      url: '/translate',
      method: 'post',
      headers: {
        'Ocp-Apim-Subscription-Key': TRANSLATOR_API_KEY,
        'Ocp-Apim-Subscription-Region': TRANSLATOR_REGION,
        'Content-type': 'application/json',
      },
      params: {
        'api-version': '3.0',
        'to': targetLanguage
      },
      data: texts.map(text => ({ 'text': text })),
      responseType: 'json'
    });

    const results = response.data;
    if (results && results.length > 0) {
      return results.map(result => 
        (result.translations && result.translations.length > 0) 
          ? result.translations[0].text 
          : ''
      );
    }
    return texts; // Return originals if translation failed
  } catch (error) {
    console.error('Batch translation error:', error);
    return texts; // Return originals on error
  }
};

/**
 * Translates message objects in the chat history
 * @param {Array} messages - Array of message objects
 * @param {string} targetLanguage - Target language code
 * @returns {Promise<Array>} - Translated message objects
 */
export const translateMessages = async (messages, targetLanguage) => {
  if (!messages || messages.length === 0 || targetLanguage === SUPPORTED_LANGUAGES.ENGLISH.code) {
    return messages;
  }

  try {
    // Extract all content strings that need translation
    const textsToTranslate = messages
      .filter(msg => msg.content && typeof msg.content === 'string')
      .map(msg => msg.content);
    
    // Also gather any transcript text from audio messages
    messages.forEach(msg => {
      if (msg.isAudio && msg.text) {
        textsToTranslate.push(msg.text);
      }
    });

    // Perform batch translation
    const translatedTexts = await batchTranslate(textsToTranslate, targetLanguage);
    
    // Map translated content back to message objects
    let translationIndex = 0;
    return messages.map(msg => {
      const newMsg = { ...msg };
      
      if (newMsg.content && typeof newMsg.content === 'string') {
        newMsg.content = translatedTexts[translationIndex++];
      }
      
      if (newMsg.isAudio && newMsg.text) {
        newMsg.text = translatedTexts[translationIndex++];
      }
      
      return newMsg;
    });
  } catch (error) {
    console.error('Error translating messages:', error);
    return messages; // Return original messages on error
  }
};